import * as THREE from 'three';
import { MeshBVH, CENTER, SAH } from 'three-mesh-bvh';
import { PLAYER, SURFACES } from '../core/Constants.js';

/**
 * CollisionWorld - mundo de colisao estatico (secao 4.6 do ARCHITECTURE.md).
 *
 * Cinco sistemas dependem desta API: PlayerController, Ballistics, BotManager,
 * Navmesh e FX. Ela nao muda.
 *
 * DECISOES DE PROJETO
 *
 * 1) UMA BVH SO, sobre uma geometria mesclada em ESPACO DE MUNDO.
 *    Uma BVH por mesh obrigaria a percorrer N arvores por consulta e a converter
 *    origem/direcao para o espaco local de cada uma. Mesclando tudo em espaco de
 *    mundo, a consulta e uma unica travessia e o resultado ja sai em mundo -
 *    o que elimina o erro classico de devolver a normal LOCAL da face e o
 *    decalque de bala nascer virado errado.
 *
 * 2) BVH em modo `indirect`.
 *    No modo normal a lib REORDENA o index buffer da geometria durante a
 *    construcao, e o indice de triangulo devolvido nas consultas passa a ser o
 *    da ordem nova. Isso destruiria o mapa "triangulo -> superficie/objeto" que
 *    montamos na mesclagem. Em modo indirect o index nao e tocado e todos os
 *    callbacks devolvem o indice ORIGINAL do triangulo.
 *
 * 3) Raycast proprio por shapecast, em vez de bvh.raycastFirst().
 *    O raycastFirst da lib aloca (point.clone(), normal, barycoord) a cada
 *    triangulo atingido. Aqui a travessia e ordenada de frente para tras pelo
 *    `boundsTraverseOrder`, o corte usa a distancia do melhor acerto ate agora,
 *    e o resultado e escrito em temporarios de modulo: zero alocacao.
 *
 * 4) Deteccao de chao em anel de raios verticais, e nao so por contato da capsula.
 *    A capsula tem base ARREDONDADA: parada na borda de um degrau ela nao encosta
 *    no piso de cima e "flutuaria". O anel faz o personagem se comportar como um
 *    cilindro para efeito de chao - que e como todo FPS resolve isso - sem abrir
 *    mao da capsula para a colisao propriamente dita.
 *
 * Nenhum metodo publico lanca excecao. Sem geometria registrada, o mundo degrada
 * para um plano implicito em y=0 (com um unico console.warn), para o jogo
 * continuar jogavel enquanto o Level ainda nao existe.
 */

// ---------------------------------------------------------------------------
// Constantes de sintonia (as de jogo vem de Constants.js; estas sao numericas,
// do solver, e nao tem sentido fora daqui)
// ---------------------------------------------------------------------------
const DEG2RAD = Math.PI / 180;
const DEFAULT_SURFACE = 'concrete';

const MAX_SUBSTEPS = 8;          // teto de subpassos por chamada de capsuleMove
const MAX_CONTACTS = 8;          // planos de contato considerados por subpasso
const RESOLVE_PASSES = 2;        // passadas de despenetracao por subpasso
const SKIN = 0.002;              // folga ao assentar no chao (evita oscilar no zero)
const GROUND_TOL = 0.06;         // quanto o chao pode estar abaixo do pe e ainda contar
const PROBE_UP = 0.10;           // quanto o probe de chao comeca ACIMA do pe
const SNAP_DIST = 0.45;          // queda maxima colada ao chao ao descer rampa/degrau
const SNAP_MAX_VY = 0.6;         // acima disso esta subindo (pulo): nao cola no chao
const LAND_KEEP_VY = 3.0;        // queda mais rapida que isto preserva vy p/ o impacto
const STEP_MIN_GAIN = 0.02;      // ganho minimo de altura para valer como degrau
const STEP_ASSIST = 0.5;         // avanco extra no step-up, em fracao do raio
const CONTACT_DEDUP = 0.985;     // cos entre normais para tratar como o mesmo plano
const QUERY_MARGIN = 0.06;       // folga da caixa de consulta da capsula
const RING_SCALE = 0.95;         // raio do anel de probe, em fracao do raio da capsula

const MAX_TRIANGLES = 1500000;   // teto duro de memoria da BVH
const SAH_LIMIT = 60000;         // acima disso, CENTER (SAH fica caro demais no load)

const SURFACE_SET = new Set(SURFACES);

// ---------------------------------------------------------------------------
// Temporarios de modulo.
//
// TUDO que o caminho quente usa mora aqui. Nada de `new` dentro de update,
// fixedUpdate, raycast, capsuleMove, lineOfSight ou dos callbacks de shapecast.
// Como o jogo tem uma unica instancia de CollisionWorld e o JS e monothread,
// nenhuma consulta acontece dentro de outra - o estado compartilhado e seguro.
// ---------------------------------------------------------------------------
const _vA = new THREE.Vector3();
const _vB = new THREE.Vector3();
const _vC = new THREE.Vector3();

const _mat = new THREE.Matrix4();
const _matInst = new THREE.Matrix4();

// capsula / despenetracao
const _capSeg = new THREE.Line3();
const _queryBox = new THREE.Box3();
const _triPoint = new THREE.Vector3();
const _capPoint = new THREE.Vector3();
const _pushDir = new THREE.Vector3();
const _faceNormal = new THREE.Vector3();
const _saveStart = new THREE.Vector3();
const _saveEnd = new THREE.Vector3();

const _contactN = [];   // normal real do contato (classificacao)
const _contactP = [];   // plano usado para cortar a velocidade
for (let i = 0; i < MAX_CONTACTS; i++) {
  _contactN.push(new THREE.Vector3());
  _contactP.push(new THREE.Vector3());
}

let _capR = 0.35;
let _contactCount = 0;
let _hadContact = false;
let _wallContact = false;
let _ceilContact = false;
let _groundHit = false;
let _groundNY = -2;
const _groundNormal = new THREE.Vector3(0, 1, 0);
const _moveNormal = new THREE.Vector3(0, 1, 0);

let _cosMaxSlope = Math.cos(PLAYER.MAX_SLOPE_DEG * DEG2RAD);

// raycast
const _ray = new THREE.Ray();
const _hitPoint = new THREE.Vector3();
const _hitNormal = new THREE.Vector3();
const _tmpHit = new THREE.Vector3();
let _hitDist = Infinity;
let _hitTri = -1;
let _rayFar = 200;
let _rayFarSq = 40000;
let _rayCull = false;
let _rInvX = 0, _rInvY = 0, _rInvZ = 0;

// probe de chao (anel de raios verticais)
const _gpX = new Float64Array(5);
const _gpZ = new Float64Array(5);
const _gpNormal = new THREE.Vector3(0, 1, 0);
const _gpBestNormal = new THREE.Vector3(0, 1, 0);
let _gpCount = 5;
let _gpMinY = 0;
let _gpMaxY = 0;
let _gpBestWalkY = -Infinity;
let _gpBestTri = -1;

// ---------------------------------------------------------------------------
// Callbacks de shapecast - criados UMA vez. Se fossem literais por chamada,
// cada consulta alocaria um objeto e tres closures.
// ---------------------------------------------------------------------------

/** Distancia de entrada do raio na caixa (slab test). Infinity se nao entra. */
function _rayBoxEnter(box) {
  const o = _ray.origin;
  const d = _ray.direction;
  const mn = box.min;
  const mx = box.max;
  let tmin = 0;
  let tmax = Infinity;
  let t1, t2, t;

  if (d.x > -1e-8 && d.x < 1e-8) {
    if (o.x < mn.x || o.x > mx.x) return Infinity;
  } else {
    t1 = (mn.x - o.x) * _rInvX;
    t2 = (mx.x - o.x) * _rInvX;
    if (t1 > t2) { t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmax < tmin) return Infinity;
  }

  if (d.y > -1e-8 && d.y < 1e-8) {
    if (o.y < mn.y || o.y > mx.y) return Infinity;
  } else {
    t1 = (mn.y - o.y) * _rInvY;
    t2 = (mx.y - o.y) * _rInvY;
    if (t1 > t2) { t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmax < tmin) return Infinity;
  }

  if (d.z > -1e-8 && d.z < 1e-8) {
    if (o.z < mn.z || o.z > mx.z) return Infinity;
  } else {
    t1 = (mn.z - o.z) * _rInvZ;
    t2 = (mx.z - o.z) * _rInvZ;
    if (t1 > t2) { t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmax < tmin) return Infinity;
  }

  return tmin;
}

/** Raycast do mais proximo: percorre ordenado e poda pelo melhor acerto ja visto. */
const _rayCallbacks = {
  boundsTraverseOrder: _rayBoxEnter,
  intersectsBounds: (box, isLeaf, score) => {
    const limit = _hitDist < _rayFar ? _hitDist : _rayFar;
    const s = score === undefined ? _rayBoxEnter(box) : score;
    return s < limit;
  },
  intersectsTriangle: (tri, triIndex) => {
    const p = _ray.intersectTriangle(tri.a, tri.b, tri.c, _rayCull, _tmpHit);
    if (p === null) return false;
    const dist = _tmpHit.distanceTo(_ray.origin);
    if (dist > _rayFar || dist >= _hitDist) return false;
    _hitDist = dist;
    _hitTri = triIndex;
    _hitPoint.copy(_tmpHit);
    THREE.Triangle.getNormal(tri.a, tri.b, tri.c, _hitNormal);
    return false; // segue procurando: queremos o mais proximo, nao o primeiro
  }
};

/** Linha de visao: sem ordenacao, aborta no PRIMEIRO triangulo dentro do trecho. */
const _losCallbacks = {
  intersectsBounds: (box) => _rayBoxEnter(box) < _rayFar,
  intersectsTriangle: (tri) => {
    if (_ray.intersectTriangle(tri.a, tri.b, tri.c, false, _tmpHit) === null) return false;
    // true aborta a travessia na hora - e o que torna esta versao barata
    return _tmpHit.distanceToSquared(_ray.origin) <= _rayFarSq;
  }
};

/** Despenetracao da capsula. Move o segmento DENTRO do callback (resolucao sequencial). */
const _resolveCallbacks = {
  intersectsBounds: (box) => box.intersectsBox(_queryBox),
  intersectsTriangle: (tri) => {
    const dist = tri.closestPointToSegment(_capSeg, _triPoint, _capPoint);
    if (dist >= _capR) return false;

    // contato pertence a base da capsula? So a base pode ser considerada chao -
    // sem isto, a quina de um parapeito na altura do peito viraria "chao".
    const isBottom = _capPoint.y <= _capSeg.start.y + 0.05;

    _pushDir.subVectors(_capPoint, _triPoint);
    const len = _pushDir.length();
    let depth;

    if (len > 1e-6) {
      _pushDir.multiplyScalar(1 / len);
      THREE.Triangle.getNormal(tri.a, tri.b, tri.c, _faceNormal);
      // Alinhamento forte e NEGATIVO com a normal da face significa que o ponto
      // mais proximo esta no INTERIOR da face e a capsula esta ATRAS dela, ou
      // seja, dentro do solido. Empurrar por (capPoint - triPoint) afundaria
      // ainda mais; a saida correta e pela frente da face.
      if (_pushDir.dot(_faceNormal) < -0.98 && _faceNormal.lengthSq() > 0.5) {
        _pushDir.copy(_faceNormal);
        depth = _capR + len;
      } else {
        depth = _capR - len;
      }
    } else {
      // eixo exatamente sobre o plano do triangulo: nao ha direcao a extrair
      THREE.Triangle.getNormal(tri.a, tri.b, tri.c, _pushDir);
      if (_pushDir.lengthSq() < 0.5) return false; // triangulo degenerado
      depth = _capR;
    }

    const maxPush = _capR * 2;
    if (depth > maxPush) depth = maxPush;

    _capSeg.start.addScaledVector(_pushDir, depth);
    _capSeg.end.addScaledVector(_pushDir, depth);
    _hadContact = true;
    _addContact(_pushDir, isBottom);
    return false;
  }
};

/** Overlap puro: aborta no primeiro triangulo dentro do raio. */
const _overlapCallbacks = {
  intersectsBounds: (box) => box.intersectsBox(_queryBox),
  intersectsTriangle: (tri) => tri.closestPointToSegment(_capSeg, _triPoint, _capPoint) < _capR
};

/**
 * Probe de chao. Em vez de 5 raycasts (5 travessias), faz UMA travessia e testa
 * os 5 pontos do anel contra cada triangulo. Como os raios sao verticais, o
 * teste vira um "ponto dentro do triangulo" em XZ com interpolacao de Y - bem
 * mais barato que 5 intersecoes raio-triangulo completas.
 */
const _groundCallbacks = {
  intersectsBounds: (box) => box.intersectsBox(_queryBox),
  intersectsTriangle: (tri, triIndex) => {
    const a = tri.a, b = tri.b, c = tri.c;

    let lo = a.y, hi = a.y;
    if (b.y < lo) lo = b.y; else if (b.y > hi) hi = b.y;
    if (c.y < lo) lo = c.y; else if (c.y > hi) hi = c.y;
    if (hi < _gpMinY || lo > _gpMaxY) return false;

    // area projetada em XZ; ~0 significa triangulo vertical - parede nao e chao
    const den = (b.z - c.z) * (a.x - c.x) + (c.x - b.x) * (a.z - c.z);
    if (den > -1e-9 && den < 1e-9) return false;
    const inv = 1 / den;

    let normalReady = false;
    for (let i = 0; i < _gpCount; i++) {
      const px = _gpX[i];
      const pz = _gpZ[i];
      const u = ((b.z - c.z) * (px - c.x) + (c.x - b.x) * (pz - c.z)) * inv;
      if (u < -1e-6 || u > 1.000001) continue;
      const v = ((c.z - a.z) * (px - c.x) + (a.x - c.x) * (pz - c.z)) * inv;
      if (v < -1e-6) continue;
      const w = 1 - u - v;
      if (w < -1e-6) continue;

      const y = a.y * u + b.y * v + c.y * w;
      if (y < _gpMinY || y > _gpMaxY) continue;
      if (y <= _gpBestWalkY) continue;

      if (!normalReady) {
        THREE.Triangle.getNormal(a, b, c, _gpNormal);
        normalReady = true;
      }
      // abs(): superficie quase horizontal e chao mesmo que o Level tenha
      // construido a face com a orientacao invertida. A normal guardada e
      // sempre forcada para cima.
      const ny = _gpNormal.y < 0 ? -_gpNormal.y : _gpNormal.y;
      if (ny < _cosMaxSlope) continue;

      _gpBestWalkY = y;
      _gpBestTri = triIndex;
      if (_gpNormal.y < 0) _gpBestNormal.set(-_gpNormal.x, -_gpNormal.y, -_gpNormal.z);
      else _gpBestNormal.copy(_gpNormal);
    }
    return false;
  }
};

// ---------------------------------------------------------------------------
// Helpers do solver (fora da classe: usam os temporarios de modulo)
// ---------------------------------------------------------------------------

function _addContact(n, isBottom) {
  if (isBottom && n.y >= _cosMaxSlope) {
    _groundHit = true;
    if (n.y > _groundNY) {
      _groundNY = n.y;
      _groundNormal.copy(n);
    }
  }
  if (n.y < _cosMaxSlope && n.y > -0.05) _wallContact = true;
  if (n.y <= -_cosMaxSlope) _ceilContact = true;

  for (let i = 0; i < _contactCount; i++) {
    if (_contactN[i].dot(n) > CONTACT_DEDUP) return; // plano praticamente igual
  }
  if (_contactCount >= MAX_CONTACTS) return;

  const idx = _contactCount++;
  _contactN[idx].copy(n);
  const p = _contactP[idx];

  if (n.y >= _cosMaxSlope || n.y <= -0.05) {
    // chao andavel ou teto/saliencia: corta pela normal real
    p.copy(n);
  } else {
    // Parede OU rampa ingreme demais. Cortar pela normal real deixaria o
    // jogador subir a rampa proibida como escada rolante (a componente
    // horizontal viraria movimento para cima). Achatando a normal, a rampa
    // ingreme se comporta como parede no horizontal e a gravidade continua
    // escorregando o jogador para baixo, que e o comportamento desejado.
    p.set(n.x, 0, n.z);
    const l = p.length();
    if (l > 1e-4) p.multiplyScalar(1 / l);
    else p.copy(n);
  }
}

function _clipVelocity(vel) {
  // Duas passadas: cortar contra o plano B pode reintroduzir movimento contra o
  // plano A. Uma segunda passada resolve a quina sem tremer.
  for (let pass = 0; pass < 2; pass++) {
    let changed = false;
    for (let i = 0; i < _contactCount; i++) {
      const p = _contactP[i];
      const d = vel.x * p.x + vel.y * p.y + vel.z * p.z;
      if (d < -1e-5) {
        vel.x -= p.x * d;
        vel.y -= p.y * d;
        vel.z -= p.z * d;
        changed = true;
      }
    }
    if (!changed) break;
  }
}

function _setCapsule(x, feetY, z, radius, height) {
  const r = Math.min(radius, height * 0.5);
  _capR = r;
  _capSeg.start.set(x, feetY + r, z);
  _capSeg.end.set(x, feetY + Math.max(height - r, r), z);
}

function _segTranslate(dx, dy, dz) {
  _capSeg.start.x += dx; _capSeg.start.y += dy; _capSeg.start.z += dz;
  _capSeg.end.x += dx; _capSeg.end.y += dy; _capSeg.end.z += dz;
}

function _resolve(bvh) {
  _contactCount = 0;
  _wallContact = false;
  _ceilContact = false;
  _groundHit = false;
  _groundNY = -2;

  for (let pass = 0; pass < RESOLVE_PASSES; pass++) {
    _hadContact = false;
    _queryBox.makeEmpty();
    _queryBox.expandByPoint(_capSeg.start);
    _queryBox.expandByPoint(_capSeg.end);
    _queryBox.expandByScalar(_capR + QUERY_MARGIN);
    bvh.shapecast(_resolveCallbacks);
    // sem contato nenhum nao ha o que refinar: economiza a segunda travessia
    if (!_hadContact) break;
  }
}

/**
 * Procura a superficie andavel mais alta sob o anel da capsula, na faixa
 * [feetY - drop, feetY + PROBE_UP]. Nao move nada; so mede.
 * Resultado em _gpBestWalkY / _gpBestNormal / _gpBestTri.
 */
function _probeGround(bvh, px, pz, feetY, drop, radius) {
  const rr = radius * RING_SCALE;
  _gpX[0] = px;        _gpZ[0] = pz;
  _gpX[1] = px + rr;   _gpZ[1] = pz;
  _gpX[2] = px - rr;   _gpZ[2] = pz;
  _gpX[3] = px;        _gpZ[3] = pz + rr;
  _gpX[4] = px;        _gpZ[4] = pz - rr;
  _gpCount = 5;

  _gpMinY = feetY - drop;
  _gpMaxY = feetY + PROBE_UP;
  _gpBestWalkY = -Infinity;
  _gpBestTri = -1;

  _queryBox.min.set(px - rr, _gpMinY, pz - rr);
  _queryBox.max.set(px + rr, _gpMaxY, pz + rr);
  bvh.shapecast(_groundCallbacks);

  // So conta superficie ANDAVEL. Estar inteiramente sobre uma rampa ingreme
  // nao acha nada aqui (a inclinacao reprova amostra por amostra) e o
  // personagem escorrega, que e o certo. Nao se descarta o chao por existir
  // algo mais alto e ingreme por perto: senao o jogador ficaria "no ar" so por
  // encostar no pe de uma rampa ou de um meio-fio.
  return _gpBestWalkY !== -Infinity;
}

// ---------------------------------------------------------------------------

export class CollisionWorld {
  constructor(ctx) {
    this.ctx = ctx;

    /** @type {THREE.Object3D[]} objetos registrados via addStatic */
    this._registered = [];
    /** partes efetivamente mescladas (uma por mesh; instancias entram juntas) */
    this._partStart = null;    // Uint32Array: primeiro triangulo de cada parte
    this._partSurface = [];
    this._partObject = [];
    this._partCount = 0;

    /** @type {MeshBVH|null} */
    this._bvh = null;
    this._geometry = null;
    this.triangleCount = 0;
    this.bounds = new THREE.Box3(
      new THREE.Vector3(-50, -2, -50),
      new THREE.Vector3(50, 40, 50)
    );

    this._dirty = false;
    this._built = false;
    this._minWorldY = -50;
    this._warned = new Set();
    this._buildMs = 0;

    // Resultado de capsuleMove. Reutilizado de proposito (zero alocacao por
    // chamada). `position` e `velocity` apontam para os vetores DO PROPRIO
    // CHAMADOR, que sao alterados no lugar - assim tanto
    // `this.position.copy(r.position)` quanto `this.position = r.position`
    // funcionam sem criar aliasing entre entidades diferentes.
    this._moveRes = {
      position: null,
      velocity: null,
      grounded: false,
      normal: _moveNormal,
      steppedUp: false,
      // extras (nao fazem parte do contrato, mas ajudam quem quiser)
      hitCeiling: false,
      groundSurface: null,
      groundY: 0
    };
  }

  async init() {
    _cosMaxSlope = Math.cos(PLAYER.MAX_SLOPE_DEG * DEG2RAD);
  }

  get ready() {
    return this._bvh !== null;
  }

  _warnOnce(key, msg) {
    if (this._warned.has(key)) return;
    this._warned.add(key);
    console.warn(`[Collision] ${msg}`);
  }

  // -------------------------------------------------------------------------
  // Registro e construcao
  // -------------------------------------------------------------------------

  /**
   * Registra geometria solida. Aceita Mesh, InstancedMesh, Group ou qualquer
   * Object3D - a arvore inteira e varrida em build(). Marque
   * `mesh.userData.surface` para FX e Audio escolherem particula e som; marque
   * `userData.noCollision = true` em filhos puramente decorativos.
   */
  addStatic(mesh) {
    if (!mesh) return;
    if (this._registered.indexOf(mesh) !== -1) return;
    this._registered.push(mesh);
    if (this._built) this._dirty = true;
  }

  /**
   * Constroi a BVH. Chamado por main.js depois do Level. Pode ser chamado de
   * novo (reconstrucao completa) se mais geometria for registrada depois.
   */
  build() {
    const t0 = (typeof performance !== 'undefined' ? performance.now() : 0);
    try {
      this._disposeGeometry();
      const parts = this._collectParts();

      if (parts.length === 0) {
        this._warnOnce(
          'empty',
          'nenhuma geometria estatica registrada - usando plano implicito em y=0'
        );
        this._built = true;
        this._dirty = false;
        return;
      }

      // --- passada 1: contagem exata, para alocar os buffers de uma vez ------
      let vertexTotal = 0;
      let triangleTotal = 0;
      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        vertexTotal += p.vertexCount * p.instances;
        triangleTotal += p.triangleCount * p.instances;
      }

      if (triangleTotal > MAX_TRIANGLES) {
        this._warnOnce(
          'huge',
          `cenario com ${triangleTotal} triangulos passa do teto de ${MAX_TRIANGLES}; ` +
          'a colisao pode ficar lenta'
        );
      }

      const positions = new Float32Array(vertexTotal * 3);
      const indices = new Uint32Array(triangleTotal * 3);
      const partStart = new Uint32Array(parts.length);
      this._partSurface.length = 0;
      this._partObject.length = 0;

      // --- passada 2: transformar para MUNDO e mesclar ----------------------
      let vHead = 0;   // vertices ja escritos
      let tHead = 0;   // triangulos ja escritos

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const geom = part.geometry;
        const posAttr = geom.attributes.position;
        const index = geom.index;
        const vCount = part.vertexCount;
        const tCount = part.triangleCount;

        partStart[i] = tHead;
        this._partSurface.push(part.surface);
        this._partObject.push(part.object);

        for (let inst = 0; inst < part.instances; inst++) {
          if (part.isInstanced) {
            part.object.getMatrixAt(inst, _matInst);
            _mat.multiplyMatrices(part.object.matrixWorld, _matInst);
          } else {
            _mat.copy(part.object.matrixWorld);
          }

          const vBase = vHead;
          for (let v = 0; v < vCount; v++) {
            _vA.fromBufferAttribute(posAttr, v).applyMatrix4(_mat);
            const o = (vBase + v) * 3;
            positions[o] = _vA.x;
            positions[o + 1] = _vA.y;
            positions[o + 2] = _vA.z;
          }
          vHead += vCount;

          const iBase = tHead * 3;
          if (index) {
            for (let t = 0; t < tCount * 3; t++) {
              indices[iBase + t] = vBase + index.getX(t);
            }
          } else {
            for (let t = 0; t < tCount * 3; t++) {
              indices[iBase + t] = vBase + t;
            }
          }
          tHead += tCount;
        }
      }

      this._partStart = partStart;
      this._partCount = parts.length;
      this.triangleCount = tHead;

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setIndex(new THREE.BufferAttribute(indices, 1));

      this._bvh = new MeshBVH(geometry, {
        // indirect: nao reordena o index, entao o indice de triangulo devolvido
        // continua batendo com o mapa de superficies montado acima
        indirect: true,
        strategy: tHead <= SAH_LIMIT ? SAH : CENTER,
        targetLeafSize: 8,
        verbose: false
      });
      this._geometry = geometry;

      geometry.computeBoundingBox();
      if (geometry.boundingBox) this.bounds.copy(geometry.boundingBox);
      this._minWorldY = this.bounds.min.y - 2;

      this._built = true;
      this._dirty = false;
      this._buildMs = (typeof performance !== 'undefined' ? performance.now() : 0) - t0;
    } catch (err) {
      console.warn('[Collision] falha ao construir a BVH; caindo para plano y=0:', err);
      this._bvh = null;
      this._geometry = null;
      this._built = true;
    }
  }

  /** Varre os objetos registrados e devolve a lista de partes mesclaveis. */
  _collectParts() {
    const parts = [];
    const seen = new Set();

    for (let i = 0; i < this._registered.length; i++) {
      const root = this._registered[i];
      if (!root) continue;

      // Rede de seguranca: quem usa matrixAutoUpdate=false precisa chamar
      // updateMatrix(). Se a matriz local ficou identidade mas o transform nao
      // e neutro, foi esquecimento - corrigimos antes de compor o mundo.
      root.traverse((o) => this._fixLocalMatrix(o));
      root.updateWorldMatrix(true, true);

      root.traverse((o) => {
        if (!o.isMesh) return;
        if (o.userData && o.userData.noCollision === true) return;
        if (seen.has(o)) return;
        seen.add(o);

        const geom = o.geometry;
        if (!geom || !geom.attributes || !geom.attributes.position) return;

        const posAttr = geom.attributes.position;
        const vertexCount = posAttr.count;
        if (vertexCount < 3) return;

        const triangleCount = geom.index
          ? Math.floor(geom.index.count / 3)
          : Math.floor(vertexCount / 3);
        if (triangleCount < 1) return;

        const isInstanced = o.isInstancedMesh === true;
        const instances = isInstanced ? Math.max(0, o.count | 0) : 1;
        if (instances < 1) return;

        parts.push({
          object: o,
          geometry: geom,
          vertexCount,
          triangleCount,
          instances,
          isInstanced,
          surface: this._surfaceNameOf(o)
        });
      });
    }
    return parts;
  }

  _fixLocalMatrix(o) {
    if (o.matrixAutoUpdate) return;
    const e = o.matrix.elements;
    const isIdentity =
      e[0] === 1 && e[1] === 0 && e[2] === 0 && e[3] === 0 &&
      e[4] === 0 && e[5] === 1 && e[6] === 0 && e[7] === 0 &&
      e[8] === 0 && e[9] === 0 && e[10] === 1 && e[11] === 0 &&
      e[12] === 0 && e[13] === 0 && e[14] === 0 && e[15] === 1;
    if (!isIdentity) return;
    const p = o.position, s = o.scale, q = o.quaternion;
    const neutral =
      p.x === 0 && p.y === 0 && p.z === 0 &&
      s.x === 1 && s.y === 1 && s.z === 1 &&
      q.x === 0 && q.y === 0 && q.z === 0 && q.w === 1;
    if (!neutral) o.updateMatrix();
  }

  _surfaceNameOf(o) {
    const s = o.userData && o.userData.surface;
    if (typeof s === 'string' && SURFACE_SET.has(s)) return s;
    return DEFAULT_SURFACE;
  }

  /** Busca binaria triangulo -> parte. As partes sao contiguas e crescentes. */
  _partOf(triIndex) {
    const starts = this._partStart;
    if (!starts || triIndex < 0) return -1;
    let lo = 0;
    let hi = this._partCount - 1;
    let found = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (starts[mid] <= triIndex) { found = mid; lo = mid + 1; }
      else hi = mid - 1;
    }
    return found;
  }

  _surfaceOfTriangle(triIndex) {
    const p = this._partOf(triIndex);
    return p === -1 ? DEFAULT_SURFACE : this._partSurface[p];
  }

  _objectOfTriangle(triIndex) {
    const p = this._partOf(triIndex);
    return p === -1 ? null : this._partObject[p];
  }

  // -------------------------------------------------------------------------
  // Raycast
  // -------------------------------------------------------------------------

  /** Prepara _ray e as constantes do slab test. Sem alocacao. */
  _prepareRay(origin, dir, maxDistance) {
    _ray.origin.copy(origin);
    _ray.direction.copy(dir);
    const len = _ray.direction.length();
    if (len < 1e-9) return false;
    _ray.direction.multiplyScalar(1 / len);
    _rInvX = 1 / _ray.direction.x;
    _rInvY = 1 / _ray.direction.y;
    _rInvZ = 1 / _ray.direction.z;
    _rayFar = maxDistance > 0 ? maxDistance : 0;
    _rayFarSq = _rayFar * _rayFar;
    return _rayFar > 0;
  }

  /** Nucleo do raycast. Escreve em _hitDist/_hitPoint/_hitNormal/_hitTri. */
  _castNearest(origin, dir, maxDistance, backfaceCull) {
    _hitDist = Infinity;
    _hitTri = -1;
    if (!this._bvh) return false;
    if (!this._prepareRay(origin, dir, maxDistance)) return false;
    _rayCull = !!backfaceCull;
    this._bvh.shapecast(_rayCallbacks);
    return _hitDist !== Infinity;
  }

  /**
   * Raio contra o cenario estatico.
   *
   * O `point` e a `normal` saem em ESPACO DE MUNDO (a geometria da BVH ja foi
   * mesclada em mundo), e a normal e virada para se opor a `dir` - e o que faz
   * o decalque de bala nascer de frente para quem atirou.
   *
   * @param {THREE.Vector3} origin
   * @param {THREE.Vector3} dir            nao precisa estar normalizado
   * @param {number} [maxDistance=200]
   * @param {object} [opts]
   * @param {boolean} [opts.backfaceCull=false]  ignora faces de costas
   * @param {object} [opts.target]  objeto a preencher (com .point e .normal
   *                                Vector3) para quem quiser zero alocacao
   * @returns {{point:THREE.Vector3, normal:THREE.Vector3, distance:number,
   *            object:THREE.Object3D|null, surface:string}|null}
   */
  raycast(origin, dir, maxDistance = 200, opts = {}) {
    if (!origin || !dir) return null;

    if (!this._bvh) return this._fallbackRay(origin, dir, maxDistance, opts);

    if (!this._castNearest(origin, dir, maxDistance, opts.backfaceCull === true)) return null;

    // normal sempre contra o raio (geometria de face unica pode vir invertida)
    if (_hitNormal.dot(_ray.direction) > 0) _hitNormal.multiplyScalar(-1);

    const out = opts.target || {
      point: new THREE.Vector3(),
      normal: new THREE.Vector3(),
      distance: 0,
      object: null,
      surface: DEFAULT_SURFACE
    };
    if (!out.point) out.point = new THREE.Vector3();
    if (!out.normal) out.normal = new THREE.Vector3();
    out.point.copy(_hitPoint);
    out.normal.copy(_hitNormal);
    out.distance = _hitDist;
    out.object = this._objectOfTriangle(_hitTri);
    out.surface = this._surfaceOfTriangle(_hitTri);
    return out;
  }

  /** Modo degradado: plano infinito em y=0, para o jogo seguir sem Level. */
  _fallbackRay(origin, dir, maxDistance, opts) {
    if (!this._prepareRay(origin, dir, maxDistance)) return null;
    const dy = _ray.direction.y;
    if (dy > -1e-6 && dy < 1e-6) return null;
    const t = -origin.y / dy;
    if (t < 0 || t > _rayFar) return null;

    const out = (opts && opts.target) || {
      point: new THREE.Vector3(),
      normal: new THREE.Vector3(),
      distance: 0,
      object: null,
      surface: DEFAULT_SURFACE
    };
    if (!out.point) out.point = new THREE.Vector3();
    if (!out.normal) out.normal = new THREE.Vector3();
    out.point.copy(_ray.origin).addScaledVector(_ray.direction, t);
    out.normal.set(0, dy < 0 ? 1 : -1, 0);
    out.distance = t;
    out.object = null;
    out.surface = DEFAULT_SURFACE;
    return out;
  }

  /**
   * Linha de visao entre dois pontos. Versao barata para a IA: uma travessia
   * sem ordenacao, saindo no primeiro triangulo encontrado, zero alocacao.
   * @returns {boolean} true se NAO ha geometria estatica entre a e b
   */
  lineOfSight(a, b) {
    if (!a || !b) return false;
    if (!this._bvh) return true;

    _vA.subVectors(b, a);
    const dist = _vA.length();
    if (dist < 1e-5) return true;
    _vA.multiplyScalar(1 / dist);

    // recua as pontas: origem ou destino encostados numa parede nao podem
    // bloquear a si mesmos
    const trim = 0.03;
    const far = dist - trim * 2;
    if (far <= 0) return true;

    _vB.copy(a).addScaledVector(_vA, trim);
    if (!this._prepareRay(_vB, _vA, far)) return true;

    return !this._bvh.shapecast(_losCallbacks);
  }

  // -------------------------------------------------------------------------
  // Capsula
  // -------------------------------------------------------------------------

  /**
   * Move uma capsula vertical resolvendo colisao, deslizamento, degrau e chao.
   *
   * `position` sao os PES (base da capsula). `position` e `velocity` sao
   * alterados NO LUGAR e devolvidos dentro do resultado - o objeto de resultado
   * e reaproveitado entre chamadas, entao consuma-o no ato (copie o que
   * precisar) antes de chamar de novo.
   *
   * @param {THREE.Vector3} position pes da capsula (alterado no lugar)
   * @param {THREE.Vector3} velocity m/s (alterado no lugar)
   * @param {number} dt
   * @param {number} radius
   * @param {number} height altura total da capsula
   * @param {object} [opts] {maxSlopeDeg, stepHeight, snap}
   */
  capsuleMove(position, velocity, dt, radius, height, opts) {
    const res = this._moveRes;
    res.position = position;
    res.velocity = velocity;
    res.grounded = false;
    res.steppedUp = false;
    res.hitCeiling = false;
    res.groundSurface = null;
    res.normal = _moveNormal;
    _moveNormal.set(0, 1, 0);

    if (!position || !velocity) return res;

    // saneamento: um NaN vindo de fora congelaria a entidade para sempre
    if (!Number.isFinite(velocity.x) || !Number.isFinite(velocity.y) || !Number.isFinite(velocity.z)) {
      velocity.set(0, 0, 0);
      this._warnOnce('nanvel', 'velocidade nao finita recebida em capsuleMove; zerada');
    }
    if (!Number.isFinite(position.x) || !Number.isFinite(position.y) || !Number.isFinite(position.z)) {
      this.bounds.getCenter(_vC);
      position.set(_vC.x, this.bounds.max.y, _vC.z);
      velocity.set(0, 0, 0);
      this._warnOnce('nanpos', 'posicao nao finita recebida em capsuleMove; reposicionada');
    }

    const r = Math.max(0.02, Math.min(radius, height * 0.5));
    const h = Math.max(height, r * 2);
    const step = dt > 0 ? dt : 0;
    res.groundY = position.y;

    const bvh = this._bvh;
    if (!bvh) {
      this._fallbackMove(position, velocity, step, res);
      return res;
    }

    const cosSlope = opts && opts.maxSlopeDeg !== undefined
      ? Math.cos(opts.maxSlopeDeg * DEG2RAD)
      : Math.cos(PLAYER.MAX_SLOPE_DEG * DEG2RAD);
    _cosMaxSlope = cosSlope;
    const stepHeight = opts && opts.stepHeight !== undefined ? opts.stepHeight : PLAYER.STEP_HEIGHT;
    const allowSnap = !opts || opts.snap !== false;

    // Estado inicial de chao. Custa uma travessia, mas e o que torna corretas
    // duas decisoes: se pode subir degrau e se pode colar no chao ao descer.
    const wasGrounded = _probeGround(bvh, position.x, position.z, position.y, GROUND_TOL, r);

    // --- subpassos --------------------------------------------------------
    const speed = Math.sqrt(
      velocity.x * velocity.x + velocity.y * velocity.y + velocity.z * velocity.z
    );
    const maxStepLen = r * 0.5;
    let substeps = 1;
    if (step > 0 && speed > 0) {
      substeps = Math.ceil((speed * step) / maxStepLen);
      if (substeps < 1) substeps = 1;
      if (substeps > MAX_SUBSTEPS) substeps = MAX_SUBSTEPS;
    }
    const subDt = step / substeps;
    const hardCap = r * 0.9; // teto duro por subpasso: nunca atravessa geometria

    _setCapsule(position.x, position.y, position.z, r, h);

    let grounded = false;
    let groundedNY = -2;
    let ceiling = false;
    let steppedUp = false;
    let stepTried = false;

    for (let s = 0; s < substeps; s++) {
      // integracao semi-implicita: usa a velocidade JA cortada do subpasso anterior
      let dx = velocity.x * subDt;
      let dy = velocity.y * subDt;
      let dz = velocity.z * subDt;
      const dl = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dl > hardCap) {
        const k = hardCap / dl;
        dx *= k; dy *= k; dz *= k;
      }
      _segTranslate(dx, dy, dz);

      _resolve(bvh);

      if (_groundHit && _groundNY > groundedNY) {
        grounded = true;
        groundedNY = _groundNY;
        _moveNormal.copy(_groundNormal);
      }
      if (_ceilContact) ceiling = true;

      // guarda a velocidade horizontal ANTES do corte: se o degrau der certo,
      // ela volta - senao o jogador para seco em cada degrau da escada
      const preVX = velocity.x;
      const preVZ = velocity.z;

      _clipVelocity(velocity);

      const hSpeed = Math.sqrt(preVX * preVX + preVZ * preVZ);
      if (
        !stepTried && _wallContact && hSpeed > 0.05 &&
        (grounded || wasGrounded) && stepHeight > 0.01
      ) {
        stepTried = true;
        if (this._tryStepUp(bvh, r, h, preVX, preVZ, hSpeed, subDt, stepHeight)) {
          steppedUp = true;
          grounded = true;
          groundedNY = _gpBestNormal.y;
          _moveNormal.copy(_gpBestNormal);
          velocity.x = preVX;
          velocity.z = preVZ;
          if (velocity.y < 0) velocity.y = 0;
        }
      }
    }

    position.set(_capSeg.start.x, _capSeg.start.y - _capR, _capSeg.start.z);

    // --- chao final: anel de raios (a capsula tem base redonda e nao serve
    // sozinha para decidir chao junto a borda de um degrau) -----------------
    // Subindo (pulo) nao se procura chao: alem de economizar uma travessia,
    // impede que o jogador seja grudado no topo de uma saliencia no meio do salto.
    const rising = velocity.y > SNAP_MAX_VY;
    const drop = (allowSnap && wasGrounded) ? SNAP_DIST : GROUND_TOL;
    if (!rising && _probeGround(bvh, position.x, position.z, position.y, drop, r)) {
      const gy = _gpBestWalkY;
      const delta = position.y - gy;

      if (delta <= GROUND_TOL) {
        // assenta exatamente sobre a superficie (com uma pele minima) - deixa o
        // jogador parado estavel, sem flutuar nem oscilar em torno do zero
        position.y = gy + SKIN;
        grounded = true;
        if (_gpBestNormal.y > groundedNY) {
          groundedNY = _gpBestNormal.y;
          _moveNormal.copy(_gpBestNormal);
        }
        res.groundSurface = _gpBestTri >= 0 ? this._surfaceOfTriangle(_gpBestTri) : DEFAULT_SURFACE;
        res.groundY = gy;
      } else if (drop === SNAP_DIST && delta <= SNAP_DIST) {
        // desceu rampa/degrau pequeno: cola no chao em vez de sair voando
        position.y = gy + SKIN;
        grounded = true;
        groundedNY = _gpBestNormal.y;
        _moveNormal.copy(_gpBestNormal);
        res.groundSurface = _gpBestTri >= 0 ? this._surfaceOfTriangle(_gpBestTri) : DEFAULT_SURFACE;
        res.groundY = gy;
        // queda rapida preserva vy para o controller medir o impacto do pouso
        if (velocity.y < 0 && velocity.y > -LAND_KEEP_VY) velocity.y = 0;
        _setCapsule(position.x, position.y, position.z, r, h);
        _resolve(bvh);
        position.set(_capSeg.start.x, _capSeg.start.y - _capR, _capSeg.start.z);
      }
    }

    if (grounded && res.groundSurface === null) {
      res.groundSurface = DEFAULT_SURFACE;
      res.groundY = position.y;
    }

    res.grounded = grounded;
    res.steppedUp = steppedUp;
    res.hitCeiling = ceiling;
    if (!grounded) _moveNormal.set(0, 1, 0);
    return res;
  }

  /**
   * Tenta subir um degrau de ate stepHeight. Sobe, valida teto, avanca, procura
   * o chao no topo e valida o ganho de altura. Qualquer reprovacao volta tudo.
   * @returns {boolean}
   */
  _tryStepUp(bvh, r, h, vx, vz, hSpeed, subDt, stepHeight) {
    _saveStart.copy(_capSeg.start);
    _saveEnd.copy(_capSeg.end);
    const origFeetY = _capSeg.start.y - _capR;
    const origX = _capSeg.start.x;
    const origZ = _capSeg.start.z;

    // 1. sobe e confere se cabe (se houver teto, o resolve empurra de volta)
    _segTranslate(0, stepHeight, 0);
    _resolve(bvh);
    if (_capSeg.start.y - _capR < origFeetY + stepHeight - 0.02) {
      _capSeg.start.copy(_saveStart);
      _capSeg.end.copy(_saveEnd);
      return false;
    }

    // 2. avanca na direcao do movimento. O avanco minimo existe porque a base
    // arredondada da capsula precisa passar da quina do degrau para que o anel
    // de probe encontre a superficie de cima.
    const inv = 1 / hSpeed;
    const dirX = vx * inv;
    const dirZ = vz * inv;
    const advance = Math.max(hSpeed * subDt, r * STEP_ASSIST);
    _segTranslate(dirX * advance, 0, dirZ * advance);
    _resolve(bvh);

    // 3. procura chao logo abaixo, cobrindo do topo do degrau ate o piso atual
    const raisedFeetY = _capSeg.start.y - _capR;
    const found = _probeGround(
      bvh, _capSeg.start.x, _capSeg.start.z, raisedFeetY,
      stepHeight + GROUND_TOL + 0.05, r
    );

    if (!found || _gpBestWalkY <= origFeetY + STEP_MIN_GAIN ||
        _gpBestWalkY > origFeetY + stepHeight + 0.02) {
      // sem ganho de altura => era parede, nao degrau
      _capSeg.start.copy(_saveStart);
      _capSeg.end.copy(_saveEnd);
      return false;
    }

    // 4. assenta no topo e confere que nao foi empurrado de volta
    const targetY = _gpBestWalkY + SKIN;
    _setCapsule(_capSeg.start.x, targetY, _capSeg.start.z, _capR, h);
    _resolve(bvh);

    const finalFeetY = _capSeg.start.y - _capR;
    const movedX = _capSeg.start.x - origX;
    const movedZ = _capSeg.start.z - origZ;
    const movedH = Math.sqrt(movedX * movedX + movedZ * movedZ);

    if (finalFeetY < origFeetY + STEP_MIN_GAIN || movedH < advance * 0.25) {
      _capSeg.start.copy(_saveStart);
      _capSeg.end.copy(_saveEnd);
      return false;
    }
    return true;
  }

  /** Modo degradado: plano solido em y=0. */
  _fallbackMove(position, velocity, dt, res) {
    this._warnOnce('fallbackmove', 'capsuleMove sem BVH - plano implicito em y=0');
    position.x += velocity.x * dt;
    position.y += velocity.y * dt;
    position.z += velocity.z * dt;
    if (position.y <= 0) {
      position.y = 0;
      if (velocity.y < 0) velocity.y = 0;
      res.grounded = true;
      res.groundSurface = DEFAULT_SURFACE;
      res.groundY = 0;
    }
    _moveNormal.set(0, 1, 0);
  }

  /**
   * A capsula encosta em alguma coisa nesta posicao? Util para validar spawn e
   * ponto de cobertura antes de teleportar alguem para dentro de uma parede.
   * @returns {boolean}
   */
  capsuleOverlap(position, radius, height) {
    if (!this._bvh || !position) return false;
    const r = Math.max(0.02, Math.min(radius, height * 0.5));
    const hh = Math.max(height, r * 2);
    _setCapsule(position.x, position.y, position.z, r, hh);
    _queryBox.makeEmpty();
    _queryBox.expandByPoint(_capSeg.start);
    _queryBox.expandByPoint(_capSeg.end);
    _queryBox.expandByScalar(_capR + QUERY_MARGIN);
    return this._bvh.shapecast(_overlapCallbacks);
  }

  /**
   * Altura do chao em (x, z), olhando de cima para baixo a partir de maxY.
   * Usado pela navegacao. A normal sai em espaco de mundo, sempre apontando
   * para cima.
   * @returns {{y:number, normal:THREE.Vector3, surface:string}|null}
   */
  sampleGround(x, z, maxY = 50) {
    if (!this._bvh) {
      return { y: 0, normal: new THREE.Vector3(0, 1, 0), surface: DEFAULT_SURFACE };
    }
    _vA.set(x, maxY, z);
    _vB.set(0, -1, 0);
    const range = maxY - this._minWorldY;
    if (range <= 0) return null;
    if (!this._castNearest(_vA, _vB, range, false)) return null;

    if (_hitNormal.y < 0) _hitNormal.multiplyScalar(-1);
    return {
      y: _hitPoint.y,
      normal: new THREE.Vector3(_hitNormal.x, _hitNormal.y, _hitNormal.z),
      surface: this._surfaceOfTriangle(_hitTri)
    };
  }

  /** Diagnostico (usado pelo DebugApi, se quiser). */
  stats() {
    return {
      ready: this._bvh !== null,
      meshes: this._partCount,
      triangles: this.triangleCount,
      buildMs: Math.round(this._buildMs),
      dirty: this._dirty
    };
  }

  _disposeGeometry() {
    if (this._geometry) {
      this._geometry.dispose();
      this._geometry = null;
    }
    this._bvh = null;
  }

  dispose() {
    this._disposeGeometry();
    this._registered.length = 0;
    this._partSurface.length = 0;
    this._partObject.length = 0;
    this._partStart = null;
    this._partCount = 0;
    this.triangleCount = 0;
    this._built = false;
  }
}
